import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
// @ts-expect-error importing untyped JS module into TypeScript
import { logEvent } from '@/services/clients/firebase'
// @ts-expect-error import path to avoid circular dependency issues
import store from '@/store'
import { useConsoleTourStore } from '@/stores/consoleTour'

/**
 * Which What's New items this person has seen, and whether the panel is open.
 *
 * Seen-state lives in server-side user preferences (`whatsNew.seen`) so it follows the person
 * across browsers and organizations, mirrored to localStorage so the first render does not flash
 * a banner they already dismissed while the session request is still out.
 *
 * Seen only ever grows, so reconciling the two is a union -- no "which copy wins" question. Ids
 * this build does not recognize are kept, never pruned: a newer console in another tab, or one
 * deployed and then rolled back, may have written them, and dropping them would bring its items
 * back.
 *
 * To bring the banner back while testing, use "Reset seen" at the foot of the panel. It is there
 * in local development and for Fareclock staff signed in with sudo, and clears all three copies:
 * clearing only localStorage is not enough, as the server copy merges back on load.
 *
 * The banner still needs canAutoShow below, and a CTA whose action is unavailable here (e.g.
 * customizeSidebar below the md breakpoint) is hidden.
 */

const STORAGE_KEY = 'CONSOLE_WHATS_NEW_SEEN'

/** Item id -> when it was seen (ISO). */
export type SeenMap = Record<string, string>

export interface WhatsNewPreference {
  seen: SeenMap
}

export type WhatsNewSource = 'banner' | 'help_menu'

function normalizeSeen (value: unknown): SeenMap {
  const seen = (value as WhatsNewPreference | undefined)?.seen
  if (!seen || typeof seen !== 'object') {
    return {}
  }
  return Object.fromEntries(Object.entries(seen).filter(([, at]) => typeof at === 'string'))
}

function readMirror (): SeenMap {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? normalizeSeen({ seen: JSON.parse(raw) }) : {}
  } catch {
    // Private browsing, blocked storage, or corrupt JSON. Server preferences still apply.
    return {}
  }
}

function writeMirror (seen: SeenMap) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seen))
  } catch {
    // Worst case a fresh session shows the banner until the session request lands.
  }
}

export const useWhatsNewStore = defineStore('whatsNew', () => {
  const seen = ref<SeenMap>(readMirror())
  const panelOpen = ref(false)

  // A save that fails rolls the preferences back, which this watcher sees as the server lacking
  // ids we have. Re-pushing then is right once (a save lost on the last visit); every time would
  // retry forever while offline.
  let repushed = false

  // settingsLoaded too: with no stored whatsNew at all, the session load would not change the
  // first source, and ids only the mirror holds would never reach the server.
  watch(
    () => [store.state.userPreferences?.whatsNew, store.state.settingsLoaded] as const,
    ([stored]) => {
      const server = normalizeSeen(stored)
      const merged = { ...server, ...seen.value }
      seen.value = merged
      writeMirror(merged)

      const serverIsMissingSome = Object.keys(merged).some(id => !(id in server))
      if (serverIsMissingSome && store.state.settingsLoaded && !repushed) {
        repushed = true
        void persist()
      }
    },
    { immediate: true }
  )

  async function persist () {
    try {
      await store.dispatch('patchUserPreferences', { whatsNew: { seen: seen.value } })
    } catch {
      // The mirror holds it, so this browser behaves correctly either way.
    }
  }

  function isSeen (id: string): boolean {
    return id in seen.value
  }

  /** Marks items seen. Already-seen ones keep their original time; nothing new, nothing saved. */
  function markSeen (ids: string[]) {
    const now = new Date().toISOString()
    const fresh = ids.filter(id => !isSeen(id))
    if (fresh.length === 0) {
      return Promise.resolve()
    }

    seen.value = { ...seen.value, ...Object.fromEntries(fresh.map(id => [id, now])) }
    writeMirror(seen.value)
    return persist()
  }

  /**
   * Everything that has to be true before we put news in front of someone unasked. The same bar
   * as the tour's auto-offer, plus not while the tour is running.
   */
  const canAutoShow = computed(() => {
    const state = store.state
    const getters = store.getters

    return (
      state.settingsLoaded &&
      state.organizationActive &&
      !state.fatalError &&
      !getters.needsSetup &&
      !getters.noConsoleAccess &&
      getters.insideOrgNamespace &&
      !getters.inSignUpWizard &&
      !useConsoleTourStore().isRunning
    )
  })

  /** Forgets everything seen, here and on the server. For testing; see the note above. */
  function resetSeen () {
    seen.value = {}
    writeMirror({})
    return store.dispatch('patchUserPreferences', { whatsNew: { seen: {} } })
  }

  function openPanel (source: WhatsNewSource) {
    logEvent('whats_new_panel_opened', { source })
    panelOpen.value = true
  }

  function closePanel () {
    panelOpen.value = false
  }

  return {
    seen,
    panelOpen,
    canAutoShow,
    isSeen,
    markSeen,
    resetSeen,
    openPanel,
    closePanel
  }
})
